import { useEffect, useState } from "react";
import SideMenu from "../../features/sideMenu/SideMenu";
import CustomSelect from "../../ui/select/Select";
import CustomButton from "../../ui/button/Button";
import PermissionsSelector from "./PermissionsSelector";
import {
  fetchUserById,
  updateUser,
  fetchPermitHolder,
} from "../functions/Functions";
import { useParams } from "react-router-dom";
import "./CreateUser.css";
import useConfirmDialog from "../../features/modal/ConfirmDialog";
import { useNavigate } from "react-router-dom";

const EditUser = ({
  fieldsAddUser,
  perfiles,
  companies,
  typeWorks,
  language,
  permissions,
  projects,
  clients,
  API_URL,
}) => {
  const { userId } = useParams();
  const initialPayload = {
    correo: null,
    empresa: null,
    id: null,
    lenguaje: null,
    nombre: null,
    perfil: null,
    permisos: [],
    userName: null,
    configuracionSesionNxt: {
      proyecto: null,
      cliente: null,
      permisionario: null,
      tipoTrabajo: null,
      empresa: null,
      divisionCFE: null,
      directorOperaciones: false,
    },
  };

  const [payload, setPayload] = useState(initialPayload);
  const [permisionarios, setPermisionarios] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const showDialog = useConfirmDialog();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await fetchUserById(API_URL, userId);
        setPayload(userData); // Establece los datos del usuario para editar
        if (userData.configuracionSesionNxt.cliente) {
          const permisionariosData = await fetchPermitHolder(
            API_URL,
            setPermisionarios,
            userData.configuracionSesionNxt.cliente.id
          );
          setPermisionarios(
            permisionariosData.map((permisionario) => ({
              value: permisionario.id,
              name: permisionario.razonSocial,
            }))
          );
        }
        setIsLoading(false);
      } catch (error) {
        console.error("Error al cargar los datos del usuario:", error);
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [API_URL, userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "perfil") {
      const perfilSeleccionado = perfiles.find(
        (perfil) => perfil.name === value
      );
      setPayload((prev) => ({
        ...prev,
        perfil: perfilSeleccionado || null,
      }));
    } else if (name === "lenguaje") {
      const lenguajeSeleccionado = language.find(
        (lang) => lang.prefix === value
      );
      setPayload((prev) => ({
        ...prev,
        lenguaje: lenguajeSeleccionado || null,
      }));
    } else if (
      [
        "proyecto",
        "empresa",
        "cliente",
        "permisionario",
        "tipoTrabajo",
      ].includes(name)
    ) {
      const selected = (() => {
        switch (name) {
          case "proyecto":
            return projects.find((project) => project.id === value);
          case "empresa":
            return companies.find((company) => company.id === value);
          case "cliente":
            return clients.find((client) => client.id === value);
          case "permisionario":
            return permisionarios.find(
              (permisionario) => permisionario.value === value
            );
          case "tipoTrabajo":
            return typeWorks.find((typeWork) => typeWork.id === value);
          default:
            return null;
        }
      })();
      setPayload((prev) => ({
        ...prev,
        configuracionSesionNxt: {
          ...prev.configuracionSesionNxt,
          [name]: selected || null,
        },
      }));
      if (name === "cliente" && value) {
        fetchPermitHolder(API_URL, setPermisionarios, value);
      }
    } else {
      setPayload((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handlePermissionsChange = (newPermissions) => {
    const permisosActualizados = permissions.map((permiso) => ({
      ...permiso,
      seleccionado: newPermissions.includes(String(permiso.id)),
    }));
    setPayload((prev) => ({ ...prev, permisos: permisosActualizados }));
  };

  const handleSave = async () => {
    try {
      await updateUser(API_URL, payload);
      showDialog({
        html: "El usuario fue actualizado con éxito.",
        icon: "success",
        confirmButtonText: "Aceptar",
        mostrarCancelar: false,
        onConfirm: () => {
          navigate("/usuarios");
        },
      });
    } catch (error) {
      console.error("Error al actualizar el usuario:", error);
    }
  };

  if (isLoading) return <p>Cargando datos del usuario...</p>;

  const isVisible = (fieldName) => {
    if (fieldName === "permisionario") {
      return (
        payload.perfil?.name === "PROJECT_MANAGER" &&
        payload.configuracionSesionNxt.cliente
      );
    }
    if (["empresa", "tipoTrabajo", "proyecto", "cliente"].includes(fieldName)) {
      return payload.perfil?.name === "PROJECT_MANAGER";
    }
    return true; // Otros campos son siempre visibles
  };

  return (
    <div style={{ display: "flex", height: "100%" }}>
      <div style={{ width: "12rem" }}>
        <SideMenu />
      </div>
      <div className="editUser">
        <div className="form-container">
          <form>
            {fieldsAddUser.map((field) => {
              if (!isVisible(field.name)) return null; // Ocultar campos no visibles

              return (
                <div className="form-row" key={field.name}>
                  <label className="form-label">{field.label}:</label>
                  {field.name === "perfil" ? (
                    <CustomSelect
                      options={perfiles.map((perfil) => ({
                        value: perfil.name,
                        name: perfil.descripcion,
                      }))}
                      value={payload.perfil?.name || ""}
                      name="perfil"
                      onChange={handleChange}
                      width={"500px"}
                      height={"39px"}
                      textAlign={"left"}
                      color={"#6c757d"}
                      fontSize={"14px"}
                      fontWeight={600}
                      paddingTop={"8px"}
                      marginBottom={"6px"}
                      placeholder="Seleccione un cliente"
                      overflowY={"scroll"}
                    />
                  ) : field.name === "empresa" ? (
                    <CustomSelect
                      options={companies.map((company) => ({
                        value: company.id,
                        name: company.razonSocial,
                      }))}
                      value={payload.configuracionSesionNxt.empresa?.id || ""}
                      name="empresa"
                      onChange={handleChange}
                      width={"500px"}
                      height={"39px"}
                      textAlign={"left"}
                      color={"#6c757d"}
                      fontSize={"14px"}
                      fontWeight={600}
                      paddingTop={"8px"}
                      marginBottom={"6px"}
                      placeholder="Seleccione un cliente"
                      overflowY={"scroll"}
                    />
                  ) : field.name === "lenguaje" ? (
                    <CustomSelect
                      options={language.map((lang) => ({
                        value: lang.prefix,
                        name: lang.name,
                      }))}
                      value={payload.lenguaje?.prefix || ""}
                      name="lenguaje"
                      onChange={handleChange}
                      width={"500px"}
                      height={"39px"}
                      textAlign={"left"}
                      color={"#6c757d"}
                      fontSize={"14px"}
                      fontWeight={600}
                      paddingTop={"8px"}
                      marginBottom={"6px"}
                      placeholder="Seleccione un cliente"
                      overflowY={"scroll"}
                    />
                  ) : field.name === "cliente" ? (
                    <CustomSelect
                      options={clients.map((client) => ({
                        value: client.id,
                        name: client.razonSocial,
                      }))}
                      value={payload.configuracionSesionNxt.cliente?.id || ""}
                      name="cliente"
                      onChange={handleChange}
                      width={"500px"}
                      height={"39px"}
                      textAlign={"left"}
                      color={"#6c757d"}
                      fontSize={"14px"}
                      fontWeight={600}
                      paddingTop={"8px"}
                      marginBottom={"6px"}
                      placeholder="Seleccione un cliente"
                      overflowY={"scroll"}
                    />
                  ) : field.name === "permisionario" ? (
                    <CustomSelect
                      options={permisionarios}
                      value={
                        payload.configuracionSesionNxt.permisionario?.value ||
                        ""
                      }
                      name="permisionario"
                      onChange={handleChange}
                      width={"500px"}
                      height={"39px"}
                      textAlign={"left"}
                      color={"#6c757d"}
                      fontSize={"14px"}
                      fontWeight={600}
                      paddingTop={"8px"}
                      marginBottom={"6px"}
                      placeholder="Seleccione un cliente"
                      overflowY={"scroll"}
                    />
                  ) : field.name === "permisos" ? (
                    <PermissionsSelector
                      permissions={permissions}
                      selectedPermissions={
                        payload.permisos
                          ?.filter((p) => p.seleccionado)
                          .map((p) => p.id) || []
                      }
                      onChange={handlePermissionsChange}
                    />
                  ) : (
                    <input
                      type="text"
                      className="form-input"
                      name={field.name}
                      value={payload[field.name] || ""}
                      onChange={handleChange}
                    />
                  )}
                </div>
              );
            })}

            <div className="button-container">
              <CustomButton
                text={"Guardar"}
                color={"#FFFFFF"}
                border={"none"}
                background={"#62C776"}
                fontStyle={"normal"}
                fontWeight={700}
                fontSize={"14px"}
                width={"75px"}
                height={"39px"}
                type={"button"}
                onClick={() => handleSave()}
              />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditUser;
